import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {ExceptionService, UtilsService} from '../../../xform-compat';
import { StreamService } from '../../services/sse/stream-service';
import { AuthenticationService } from '../../services';
import {CompanyBriefing} from "../../../shared/models/ai/company-briefing.model";
import {catchError} from "rxjs/operators";
import {CompanyBriefingRequest} from "../../../shared/models/ai/company-briefing-request.model";

/**
 * Data service to integrate exchanges
 */
@Injectable({
  providedIn: 'root'
})
export class TaraService extends StreamService {

  constructor(
    http: HttpClient,
    authenticationService: AuthenticationService,
    private readonly utilsService: UtilsService,
    private readonly exceptionService: ExceptionService
  ) {
    super(http, authenticationService);
  }

  public chatStream(params?: any): Observable<any> {
    const path = '/chat-stream';
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.eventSourceStream(path, httpParams);
  }

  public taraChatStream(params?: any): Observable<any> {
    const path = '/chat/stream';
    const httpParams: HttpParams = this.utilsService.generateParams(params);
    return this.eventSourceStream(path, httpParams);
  }

  /**
   * Stop all active event source streams
   */
  public stopAllStreams(): void {
    console.log('Stopping all Tara streams');
    this.closeAllEventSources();
  }

  public getCompanyBriefing(companyId: number): Observable<CompanyBriefing> {
    return this.http.get(`${this.servicePath()}/company-briefing`, { params: { companyId: String(companyId) } })
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public sendCompanyBriefing(body: CompanyBriefingRequest): Observable<any> {
    return this.http.post(`${this.servicePath()}/company-briefing`, body)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public generateCompanyDescriptions(companyIds: Array<number>) {
    return this.http.post(`${this.servicePath()}/company-description`, companyIds)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public getSessions(): Observable<Array<any>> {
    return this.http.get<Array<any>>(`${this.servicePath()}/chat/sessions`)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public getSession(sessionId: string): Observable<any> {
    return this.http.get<any>(`${this.servicePath()}/chat/sessions/${sessionId}`)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public renameSession(sessionId: string, title: string): Observable<any> {
    return this.http.post<any>(`${this.servicePath()}/chat/sessions/${sessionId}/rename`, { title })
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public deleteSession(sessionId: string): Observable<any> {
    return this.http.delete<any>(`${this.servicePath()}/chat/sessions/${sessionId}`)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public getSessionMessages(sessionId: string): Observable<any> {
    return this.http.get<any>(`${this.servicePath()}/chat/sessions/${sessionId}/messages`)
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public postAlert(alert: any): Observable<any> {
    return this.http.post(`${this.servicePath()}/chat/alert`, alert)
      .pipe(catchError(() => of(null)));
  }

  servicePath(): string {
    return '/api/ai/tara';
  }
}
